import { BookOpen, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { VitalsCardGrid } from './review/VitalCard';
import { parseBp } from '../utils/vitalsParse';
import {
  getProgressNoteBody,
  setProgressNoteBody,
} from '../utils/progressNoteBody';

function hasExtractedVitals(vitals) {
  return vitals && Object.values(vitals).some((v) => v != null && v !== '');
}

function extractedToVitalsNote(vitalsExtracted) {
  const { systolic, diastolic } = parseBp(vitalsExtracted?.bp);
  return {
    vitals: {
      bp_systolic: systolic,
      bp_diastolic: diastolic,
      pulse: vitalsExtracted?.pulse ?? null,
      temp_celsius: vitalsExtracted?.temp_celsius ?? null,
      o2_sat: vitalsExtracted?.o2_sat ?? null,
      rr: vitalsExtracted?.rr ?? null,
      pain: vitalsExtracted?.pain ?? null,
      weight_kg: null,
      blood_sugar: null,
    },
  };
}

function vitalsToExtracted(vitalsNote) {
  const v = vitalsNote.vitals || {};
  const bp =
    v.bp_systolic != null && v.bp_diastolic != null
      ? `${v.bp_systolic}/${v.bp_diastolic}`
      : v.bp_systolic != null
        ? String(v.bp_systolic)
        : null;
  return {
    bp,
    pulse: v.pulse,
    temp_celsius: v.temp_celsius,
    o2_sat: v.o2_sat,
    rr: v.rr,
    pain: v.pain,
  };
}

export default function SoapNote({
  note,
  onChange,
  onDelete = null,
  hideExtractedVitals = false,
  readOnly = false,
  showTypeHeader = true,
}) {
  const { t } = useLanguage();
  const vitals = note.vitals_extracted || {};
  const showVitals = !hideExtractedVitals && hasExtractedVitals(vitals);
  const text = getProgressNoteBody(note);

  return (
    <div className="flex flex-col gap-2.5">
      {showTypeHeader ? (
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <BookOpen className="h-5 w-5 shrink-0 text-gray-900" strokeWidth={2} />
            <h2 className="text-base font-bold text-gray-900">{t('progressNote')}</h2>
          </div>
          {onDelete ? (
            <button
              type="button"
              onClick={onDelete}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-primary"
              aria-label={t('removeNote')}
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          ) : null}
        </div>
      ) : null}

      <textarea
        value={text}
        readOnly={readOnly}
        disabled={readOnly}
        placeholder={t('progressNotePlaceholder')}
        rows={10}
        onChange={(event) => {
          if (readOnly) return;
          onChange(setProgressNoteBody(note, event.target.value));
        }}
        className="w-full resize-y rounded-xl border-0 bg-gray-100 px-3 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30"
        aria-label={t('progressNote')}
        dir="auto"
      />

      {showVitals && (
        <VitalsCardGrid
          note={extractedToVitalsNote(vitals)}
          flags={note.flags || []}
          onChange={(vitalsNote) =>
            onChange({ ...note, vitals_extracted: vitalsToExtracted(vitalsNote) })
          }
        />
      )}
    </div>
  );
}
